import { useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getStoredViewListQueryDocument } from "./stored-view.gql";

export interface UseGetStoredViewProps {
  key?: string;
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetStoredViewList = ({ key, workspaceId, enabled = true }: UseGetStoredViewProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.storedViews.list(key ?? "", workspaceId ?? ""),
    queryFn: async () => {
      if (!key || !workspaceId) {
        throw new Error("Key and workspace ID are required");
      }
      const client = createOperationClient("GetStoredViewList");
      const data = await client.request(getStoredViewListQueryDocument, {
        input: { key, workspaceId },
      });
      return data.storedViewList;
    },
    enabled: enabled && !!key && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const storedViewList = useMemo(() => {
    return queryResult.data ?? null;
  }, [queryResult.data]);

  return {
    storedViewList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
